class SubscriptionForm extends HTMLElement {
  #form;
  #includeSMS;
  #includeEmail;
  #pleaseWaitDiv;
  #submitButton;
  #thankYouMessage;
  #errorMessage;
  #ageVerificationMessage;
  #klaviyoCustomSource;
  #klaviyoListEmail;
  #klaviyoListSMS;


  connectedCallback() {
    this.#form = this.querySelector('form');
    this.#submitButton = this.#form.querySelector('[type="submit"]');

    if (!window.klaviyo) {
      this.#submitButton.disabled = true;
      return;
    }

    this.#pleaseWaitDiv = this.querySelector('[ref="please-wait"]');
    this.#submitButton = this.#form.querySelector('[type="submit"]');
    this.#thankYouMessage = this.querySelector('[ref="thank-you-message"]');
    this.#errorMessage = this.querySelector('[ref="error-message"]');
    this.#ageVerificationMessage = this.querySelector('[ref="age-verification-error-message"]');
    
    this.#includeSMS = this.hasAttribute('include-sms');
    this.#includeEmail = this.hasAttribute('include-email');
    this.#klaviyoCustomSource = this.getAttribute('klaviyo-custom-source');
    this.#klaviyoListEmail = this.getAttribute('klaviyo-list-email');
    this.#klaviyoListSMS = this.getAttribute('klaviyo-list-sms');
    

    this.#form.addEventListener('submit', this.handleSubmit.bind(this));
    
  }

  async handleSubmit(event) {
    event.stopPropagation();
    event.preventDefault();

    if (!window.klaviyo) {
      return;
    }

    this.#pleaseWaitDiv.classList.remove('hidden');
    this.#errorMessage.classList.add('hidden');
    this.#ageVerificationMessage.classList.add('hidden');
    this.#submitButton.disabled = true;

    const publicAPIKey = await klaviyo.account();
    const basePayload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                subscriptions: {},
                properties: {}
              }
            }
          }
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: null
            }
          }
        }
      }
    };

    const inputFields = this.#form.querySelectorAll('[name]:not(:disabled)');
    const birthdayField = this.#form.querySelector('[type="date"]');

    let hasEmail = false;
    let hasSMS = false;

    if (this.#klaviyoCustomSource) {
      basePayload.data.attributes.custom_source = this.#klaviyoCustomSource;
    }

    if (birthdayField && !this.#verifyAge(birthdayField.value, birthdayField.dataset.today)) {
      this.#ageVerificationMessage.classList.remove('hidden');
      this.#submitButton.disabled = false;
      this.#pleaseWaitDiv.classList.add('hidden');
      return;
    }


    for (const inputField of inputFields) {
      if (inputField.dataset.attributeKey) {
        let attributeValue = inputField.value;
        if (inputField.dataset.attributeKey == 'phone_number' && inputField.value.trim()) {
          
          // Convert phone to E.164 format
          let formattedPhoneNumber = inputField.value.trim();
          const addCountryCode = !(['+', '1'].includes(formattedPhoneNumber.charAt(0)));
          
          formattedPhoneNumber = formattedPhoneNumber.replace(/[^0-9]/g, "");
          
          if (formattedPhoneNumber) {
            if (addCountryCode) {
              formattedPhoneNumber = `+1${formattedPhoneNumber}`;
            } else {
              formattedPhoneNumber = `+${formattedPhoneNumber}`;
            }
            
            attributeValue = formattedPhoneNumber;
            
            hasSMS = true;
          }
        }
        if (inputField.dataset.attributeKey == 'email' && inputField.value.trim()) {
          hasEmail = true;
        }
        basePayload.data.attributes.profile.data.attributes[inputField.dataset.attributeKey] = attributeValue;
      } else if (inputField.dataset.customField) {
        basePayload.data.attributes.profile.data.attributes.properties[inputField.dataset.customField] = inputField.value;
      }
    }

    const payloads = [];

    if (this.#includeEmail && hasEmail && this.#includeSMS && hasSMS && this.#klaviyoListEmail == this.#klaviyoListSMS) {
      const payload = structuredClone(basePayload);
      payload.data.relationships.list.data.id = this.#klaviyoListEmail;

      payload.data.attributes.profile.data.attributes.subscriptions.email = {
        marketing: {
          consent: "SUBSCRIBED"
        }
      };
      
      payload.data.attributes.profile.data.attributes.subscriptions.sms = {
        marketing: {
          consent: "SUBSCRIBED"
        },
        transactional: {
          consent: "SUBSCRIBED"
        }
      };

      payloads.push(payload);
    } else {
      if (this.#includeEmail && hasEmail) {
        const payload = structuredClone(basePayload);
        payload.data.relationships.list.data.id = this.#klaviyoListEmail;

        payload.data.attributes.profile.data.attributes.subscriptions.email = {
          marketing: {
            consent: "SUBSCRIBED"
          }
        };
        payloads.push(payload);
      }

      if (this.#includeSMS && hasSMS) {
        const payload = structuredClone(basePayload);
        payload.data.relationships.list.data.id = this.#klaviyoListSMS;

        payload.data.attributes.profile.data.attributes.subscriptions.sms = {
          marketing: {
            consent: "SUBSCRIBED"
          },
          transactional: {
            consent: "SUBSCRIBED"
          }
        };
        payloads.push(payload);
      }
    }

    let updateSuccess = true;

    for (const payload of payloads) {
      let fetchSuccess = true;
      try {
        const response = await fetch(
          `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(publicAPIKey)}`,
          {
            method: 'POST',
            headers: {
              accept: 'application/vnd.api+json',
              revision: '2025-10-15',
              'content-type': 'application/vnd.api+json'
            },
            body: JSON.stringify(payload)
          }
        );

        if (!response.ok) {
          fetchSuccess = false;
        }
      } catch(e) {
        fetchSuccess = false;
      }

      if (!fetchSuccess) {
        updateSuccess = false;
        this.#pleaseWaitDiv.classList.add('hidden');
        this.#submitButton.disabled = false;
        this.#errorMessage.classList.remove('hidden');
        break;
      }
    }

    if (updateSuccess) {
      this.#form.classList.add('hidden');
      this.#thankYouMessage.classList.remove('hidden');
      this.#thankYouMessage.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }

  #verifyAge(birthday, today) {
    let [limitYear, limitMonth, limitDay] = String(today).split('-');

      limitYear -= 21;

      let limitYearIsLeap = false;
      if (limitYear % 400 == 0) {
        limitYearIsLeap = true;
      } else if (limitYear % 100 == 0) {
        limitYearIsLeap = true;
      } else if (limitYear % 4 == 0) {
        limitYearIsLeap = true;
      }

      if (limitMonth == 2 && limitDay == 29 && !limitYearIsLeap) {
        limitDay = 1;
        limitMonth = 3;
      }

      const limitDate = `${String(limitYear).padStart(4, '0')}-${String(limitMonth).padStart(2, '0')}-${String(limitDay).padStart(2, '0')}`;
      
      return (birthday <= limitDate);
  }
}

if (!customElements.get('subscription-form')) {
  customElements.define('subscription-form', SubscriptionForm);
}
